<script lang="ts">
    import { run } from 'svelte/legacy';
    import { createEventDispatcher, onDestroy } from 'svelte';
    import { select } from '@rxsv/core';

    import Header from 'portlet/common/components/Header.svelte';
    import SettingsShortcuts from 'portlet/common/components/SettingsShortcuts.svelte';
    import Content from 'portlet/common/components/Content.svelte';
    import DialogContent from 'portlet/common/components/portletMenu/DialogContent.svelte';

    import type { Setting } from 'portlet/models';
    import type {
        EmailSupportStore,
        EmailTemplate,
        FiltersPopupDefinition,
        MenuInteractionEventPayload,
        PortletDebugInformation,
        SettingFormInputDefinition,
        PerformanceStore,
    } from 'header/types';
    import { AppAction, Selectors } from 'portlet/store/index';
    import { PortletMenuActions } from 'header/types';
    import { getStoreContext, getLoggerContext, getModalContext } from 'portlet/common/contexts';
    import { uniq } from 'lodash-es';
    import { SettingDefinition } from 'portlet/models';
    import { isObject, Signal } from '@bayer-int/portletce-shared';
    import { nanoid } from 'nanoid';
    import * as XLSX from 'xlsx';

    interface Props {
        noheader?: boolean;
        nomenu?: boolean;
        maximized?: boolean;
        nomandatorysettinglabel?: boolean;
        externalmenudialog?: boolean;
    }

    let {
        noheader = false,
        nomenu = false,
        maximized = false,
        nomandatorysettinglabel = false,
        externalmenudialog = true,
    }: Props = $props();

    let currentMenuEvent: CustomEvent<PortletMenuActions> | null = $state(null);

    const { open } = getModalContext();
    const store = getStoreContext();
    const logger$ = getLoggerContext();

    const dispatcher = createEventDispatcher();

    const settingForm$ = store.state$.pipe(select(Selectors.settingsFormDefinitionSelector));
    const filterForm$ = store.state$.pipe(select(Selectors.filtersFormDefinitionSelector));
    const queryLink$ = store.state$.pipe(select(Selectors.queryLinkSelector));
    const debugInfo$ = store.state$.pipe(select(Selectors.portletDebugInfoSelector));
    const supportStore$ = store.state$.pipe(select(Selectors.supportEmailStateSelector));
    const performanceStore$ = store.state$.pipe(select(Selectors.performanceStateSelector));
    const infoUrl$ = store.state$.pipe(select(Selectors.infoUrlSelector));
    const dataToDownload$ = store.state$.pipe(select(Selectors.dataToDownloadSelector));
    const dataToDownloadFormat$ = store.state$.pipe(select(Selectors.dataToDownloadFormatSelector));
    let logger = $derived($logger$);
    let isDataToDownload = $derived(!!$dataToDownload$);
    $effect(() => {
        // listening for DOWNLOAD_DATA action emmited via instanceApi
        store.action$.subscribe(action => {
            if (action.type === 'DOWNLOAD_DATA' && !!$dataToDownload$) {
                downloadData();
            }
        });
    });
    onDestroy(() => {
        store.action$.unsubscribe();
    });
    run(() => {
        currentMenuEvent &&
            openMenuDialog(
                currentMenuEvent,
                $settingForm$,
                $filterForm$,
                $debugInfo$,
                $supportStore$,
                // Temporary disabled performance
                // $performanceStore$,
            );
    });
    const onFilterReload = () => {
        store.action$.next(AppAction.view.RELOAD_DATA(new Date().toISOString()));
        store.action$.next(AppAction.performance.RESET_PERFORMANCE_DATA_CALLS_TIME());
    };

    const onGenericSettingChange = (setting: Setting) =>
        store.action$.next(AppAction.filters.CHANGE_SETTING_VALUE(setting));

    const onForceLoadAllLazySettings = () =>
        store.action$.next(AppAction.filters.FORCE_LOAD_LAZY_ALL_SETTING_DEFINITIONS());
    const onForceLoadLazySetting = (setting: SettingFormInputDefinition) =>
        SettingDefinition.isKnownSettingDefinition(setting) &&
        store.action$.next(AppAction.filters.FORCE_LOAD_LAZY_SETTING_DEFINITION(setting.key));

    const onEmailDataChange = (email: EmailTemplate) =>
        store.action$.next(AppAction.email.SET_EMAIL_DATA(email));

    const onSupportFormSubmit = (email: EmailTemplate, debugInfo: PortletDebugInformation) =>
        store.action$.next(AppAction.email.SEND_MAIL_REQUESTED({ email, debugInfo }));

    const openMenuDialog = (
        menuEvent: CustomEvent<PortletMenuActions>,
        settingForm: SettingFormInputDefinition[],
        filterForm: FiltersPopupDefinition['sections'],
        debugInfo: PortletDebugInformation,
        supportStore: EmailSupportStore,
        // performanceStore: PerformanceStore,
    ): void => {
        const event = constructMenuEvent(
            menuEvent.type,
            menuEvent.detail,
            settingForm,
            filterForm,
            debugInfo,
            supportStore,
            // performanceStore,
            clearCurrentMenuEvent,
        );
        if (!event) return;
        const [eventType, eventPayload] = event;
        dispatchMenuAction(eventType, eventPayload, menuEvent.detail);
    };

    function dispatchMenuAction(
        eventType: string,
        eventPayload: MenuInteractionEventPayload,
        menuEventName: PortletMenuActions,
    ) {
        externalmenudialog
            ? dispatcher(eventType, eventPayload)
            : open(
                  DialogContent,
                  { dialogDefinition: eventPayload },
                  { title: `Open ${menuEventName}` },
                  { onClosed: clearCurrentMenuEvent },
              );
    }

    function constructMenuEvent(
        type: string,
        actionType: PortletMenuActions,
        settingForm: SettingFormInputDefinition[],
        filterForm: FiltersPopupDefinition['sections'],
        debugInfo: PortletDebugInformation,
        supportStore: EmailSupportStore,
        // performanceStore: PerformanceStore,
        onClose: () => void,
    ): [string, MenuInteractionEventPayload] | null {
        switch (actionType) {
            case PortletMenuActions.SETTINGS: {
                const userSettingFields = settingForm.filter(
                    setting => setting.scope === undefined || setting.scope === 'user',
                );

                const designerSettingFields = settingForm.filter(
                    setting => setting.scope === 'designer',
                );

                return [
                    type,
                    {
                        tag: actionType,
                        fields: {
                            userSettingFields,
                            designerSettingFields,
                            technicalInformation: [],
                        },
                        onChange: field =>
                            onGenericSettingChange({
                                key: field.key,
                                value: field.value,
                            }),
                        forceLoadLazySetting: onForceLoadLazySetting,
                        forceLoadAllLazySettings: onForceLoadAllLazySettings,
                        onSubmit: logger.warn,
                        onClose,
                    },
                ];
            }
            case PortletMenuActions.INFO: {
                return [
                    type,
                    {
                        tag: actionType,
                        debugInfo: debugInfo,
                        onClose,
                    },
                ];
            }
            // case PortletMenuActions.PERFORMANCE: {
            //     return [
            //         type,
            //         {
            //             tag: actionType,
            //             performanceStore: performanceStore,
            //             onClose,
            //         },
            //     ];
            // }
            case PortletMenuActions.FILTERS: {
                return [
                    type,
                    {
                        tag: actionType,
                        sections: filterForm,
                        onReload: onFilterReload,
                        onClose,
                    },
                ];
            }
            case PortletMenuActions.SUPPORT: {
                return [
                    type,
                    {
                        tag: actionType,
                        debugInfo: debugInfo,
                        supportStore: supportStore,
                        onSubmit: (email, debugInfo) => onSupportFormSubmit(email, debugInfo),
                        onChange: email => onEmailDataChange(email),
                        onClose,
                    },
                ];
            }
            default:
                return null;
        }
    }

    function setCurrentMenuEvent(event: CustomEvent<PortletMenuActions>) {
        currentMenuEvent = event;
    }

    function clearCurrentMenuEvent() {
        currentMenuEvent = null;
    }

    function handlePortletMaximize() {
        dispatchMenuAction(
            'portlet-maximize',
            { tag: PortletMenuActions.MAXIMIZE },
            PortletMenuActions.MAXIMIZE,
        );
    }

    function handleJumpToAO() {
        if (!$queryLink$) return;

        window.open($queryLink$, '_blank');
    }

    function openInfoUrl() {
        if (!$infoUrl$) return;
        window.open($infoUrl$, '_blank');
    }

    function downloadData() {
        if (!isDataToDownload || !$dataToDownload$) return;
        if ($dataToDownloadFormat$ === 'csv') downloadCSV($dataToDownload$);
        if ($dataToDownloadFormat$ === 'xlsx') downloadXLSX($dataToDownload$);
    }

    function convertToCSV(data: Record<string, unknown>[], columns: string[]) {
        const csvRows = data.map((item: Record<string, unknown>) => {
            const row = columns.map(col => {
                const value = item[col];
                if (isObject(value)) {
                    return JSON.stringify(value);
                }
                return typeof value === 'string' ? `"${value}"` : value;
            });
            return row.join(',');
        });

        csvRows.unshift(columns.join(','));
        return csvRows.join('\n');
    }

    function downloadCSV(signal: Signal) {
        const columns: string[] = uniq(
            signal.value.flatMap((sig: Record<string, unknown>) => Object.keys(sig)),
        );
        const csvData = convertToCSV(signal.value, columns);
        const blob = new Blob([csvData], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = nanoid();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    function downloadXLSX(signal: Signal) {
        const columns: string[] = uniq(
            signal.value.flatMap((sig: Record<string, unknown>) => Object.keys(sig)),
        );

        const data = signal.value.map((item: Record<string, unknown>) => {
            const row: Record<string, unknown> = {};
            columns.forEach(col => {
                let value = item[col];
                if (typeof value === 'object' && value !== null) {
                    value = JSON.stringify(value);
                }
                row[col] = value ?? '';
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(data, { header: columns });
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, nanoid() + '.xlsx');
    }

    function onInteractiveTitleChange(event: CustomEvent<SettingFormInputDefinition>) {
        onGenericSettingChange({
            key: event.detail.key,
            value: event.detail.value,
        });
    }
</script>

{#if !noheader}
    <Header
        {nomenu}
        {maximized}
        {isDataToDownload}
        externalInfoUrlEnabled={!!$infoUrl$}
        on:portlet-maximize={handlePortletMaximize}
        on:portlet-menu-item-click={setCurrentMenuEvent}
        on:jump-to-ao={handleJumpToAO}
        on:interactive-title-change={onInteractiveTitleChange}
        on:open-info-url={openInfoUrl}
        on:download-data={downloadData}
    >
        <slot name="header-prefix" slot="header-prefix" />
        <slot name="header-postfix" slot="header-postfix" />
    </Header>
{/if}
<SettingsShortcuts />
<Content {nomandatorysettinglabel} on:chartlib-interaction />
